// @ts-ignore
import Numeral from "numeral"
import dayjs, { Dayjs } from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import utc from "dayjs/plugin/utc"
dayjs.extend(relativeTime)
dayjs.extend(utc)

/*
  ============================================================================
  NUMBERS
  ============================================================================
*/

// 2500000 -> $2.5m
// 500000 -> $500k
export function formatCurrency(value: number) {
  return Numeral(value).format("$0,0")
}

// 2.50 -> $2.50
export function formatSmallCurrency(
  value: number,
  options?: { precision: number }
) {
  let precision = options?.precision ?? 2
  return Numeral(value).format(
    `$0${precision === 0 ? "" : `.${"0".repeat(precision)}`}`
  )
}

// 2500000 -> $2.5m
// 500000 -> $500k
// 2100 -> $2.1k
// 900 -> $900
export function formatBigCurrency(value: number) {
  if (value <= 1000) {
    return Numeral(value).format("($0a)")
  } else if (value <= 500000) {
    return Numeral(value).format("($0.0a)")
  } else {
    return Numeral(value).format("($0,0.0a)")
  }
}

export function formatSmallNumber(
  value: number,
  options?: { precision: number }
) {
  let precision = options?.precision ?? 2
  if (value <= 0.001 && value >= -0.001) return "0"
  return Numeral(value).format(
    `0${precision === 0 ? "" : `.${"0".repeat(precision)}`}`
  )
}

// 2500000 -> 2.5m
// 500000 -> 500k
// 2100 -> 2.1k
// 900 -> 900
export function formatBigNumber(value: number) {
  if (value <= 1000) {
    return Numeral(value).format(`(0a)`)
  } else if (value <= 500000) {
    return Numeral(value).format(`(0.0a)`)
  } else {
    return Numeral(value).format(`(0,0.0a)`)
  }
}

export function formatSmallPercentage(
  value: number,
  options?: { precision: number }
) {
  let precision = options?.precision ?? 0
  if (value <= 0.01) precision = 1
  if (value === 0) precision = 0
  return Numeral(value).format(
    `0${precision === 0 ? "" : `.${"0".repeat(precision)}`}%`
  )
}

// 200000 -> 200,000
export function formatWithCommas(value: number) {
  return Numeral(value).format("0,0")
}

// 0.4284578 -> 0.4
export function formatWithDecimals(value: number) {
  return Numeral(value).format("0.0")
}

/*
  ============================================================================
  DATES
  ============================================================================
*/

export function formatTime(value: number) {
  return Numeral(value).format("00:00:00")
}

export function formatTimeSince(date: string) {
  return dayjs(date).fromNow()
}
export function formatTimeSinceUtc(date: string) {
  return dayjs.utc(date).fromNow()
}

export function formatFromString({
  date,
  fromFormat,
  toFormat,
  fromUnixTimestamp,
}: {
  date: string
  fromFormat: string
  toFormat: string
  fromUnixTimestamp?: boolean
}) {
  return fromUnixTimestamp
    ? dayjs.unix(Number(date)).format(toFormat)
    : dayjs(date, fromFormat).format(toFormat)
}

// gives the raw date object that we could swap out later for another library if we needed
export function getRawDate(initDate: string | Date) {
  return initDate ? dayjs(initDate) : dayjs()
}

export function getRawPastDate(number: number, unit: string) {
  return dayjs().subtract(number, unit)
}

// gives date like "2021-10-31" for today
export function getTodaysDate() {
  return dayjs().format("YYYY-MM-DD")
}

export function getFirstDayOfMonth() {
  return dayjs().startOf("month")
}

// takes a number (like 7), and a measure (like 'day')
// and gives a date that far away from today
export function getPastDate(number: number, unit: string) {
  return dayjs().subtract(number, unit).format("YYYY-MM-DD")
}

// takes a number (like 7), and a measure (like 'day')
// and gives a date that far away from today
export function getFutureDate(
  initDate: string | Date,
  number: number,
  unit: string,
  isDateString?: boolean
) {
  if (isDateString) {
    return dayjs(initDate).add(number, unit).format("YYYY-MM-DD")
  } else {
    return dayjs(new Date(initDate)).add(number, unit).format("YYYY-MM-DD")
  }
}

// "2021-10-31 00:00:00" -> "10-31"
export function formatShortDate(
  date: string,
  options?: {
    fromFormat?: string
    includeYear?: boolean
    fromUnixTimestamp?: boolean
  }
) {
  return formatFromString({
    date,
    fromFormat: options?.fromFormat || "YYYY-MM-DD",
    toFormat: options?.includeYear ? "MM-DD-YY" : "MM-DD",
    fromUnixTimestamp: options?.fromUnixTimestamp || false,
  })
}

// "2021-10-31 00:00:00" -> "10-31-2021"
export function formatLongDate(
  date: string,
  options?: { fromFormat?: string; toFormat?: string }
) {
  const fromFormat = options?.fromFormat || "YYYY-MM-DD"
  const toFormat = options?.toFormat || "MM-DD-YYYY"
  return dayjs(date, fromFormat).format(toFormat)
}

// "2021-10-31 00:00:00" -> "Oct 31, 2021"
export function formatReadableDate(date: string | Dayjs) {
  return dayjs(date, "YYYY-MM-DD").format("MMM D, YYYY")
}

export function formatLongReadableDate(date: string | Dayjs) {
  return dayjs(date, "YYYY-MM-DD").format("MMMM D, YYYY")
}

export function formatReadableDateBlankDefault(date: string) {
  if (date) {
    if (date.length > 0) {
      return dayjs(date, "YYYY-MM-DD").format("MMM D, YYYY")
    }
  }
  return ""
}

export function formatReadableTime(date: string) {
  return dayjs(date).format("h:mma")
}

// "2021-10-31 00:00:00" -> "Oct 31, 2021"
export function formatReadableDatetime(
  date: string,
  options: { fromUnixtimestamp?: boolean; fromUtc?: boolean } = {}
) {
  let tempDate = null
  if (options.fromUnixtimestamp) {
    tempDate = dayjs.unix(Number(date))
  } else if (options.fromUtc) {
    tempDate = dayjs.utc(date).local()
  } else {
    tempDate = dayjs(date)
  }
  return tempDate.format("MMM D, YYYY [at] h:mma")
}
export function formatReadableDatetimeUtc(date: string) {
  return dayjs.utc(date).local().format("MMM D, YYYY [at] h:mma")
}

// 1636124036833 -> 8:53am
export function formatShortTimeOfDay(date: number) {
  return dayjs(date).format("h:mma")
}

// '2021-01-01'-'2022-01-03' -> Set('2021-01-01','2021-01-02','2021-01-03')
export function getRangeBetweenDates(
  startDate?: string,
  endDate?: string
): Set<string> {
  let dateSet: Set<string> = new Set()
  if (!startDate || !endDate) {
    return dateSet
  }

  let iterations = 1
  while (!dateSet.has(endDate) && iterations < 365) {
    const currentDate = dayjs(new Date(startDate)).add(iterations, "day")
    const formattedDate: string = currentDate.format("YYYY-MM-DD")
    dateSet.add(formattedDate)
    iterations += 1
  }
  return dateSet
}

// check difference between dates in units (days, months, etc)
// '2021-01-15' - '2021-01-01' -> 14
export function getDifferenceBetweenDates(
  firstDate: string | Date,
  secondDate: string | Date,
  unit?: any
) {
  const date1 = dayjs(new Date(firstDate))
  const date2 = dayjs(new Date(secondDate))
  return date1.diff(date2, unit || "day")
}

/*
  ============================================================================
  TITLES
  ============================================================================
*/

// truncates the middle of a title so you can see the variant info at the end
// Modern Eternity Khloe 3-in-1 Coat - Black \ XS -> Modern Eternity Khloe 3-i...Coat - Black \ XS
export function truncateMiddle(
  string: string,
  charCount: number,
  reverseCharCount: number
) {
  const TRUNCATION_CHAR_SIZE = charCount || 25
  const REVERSE_TRUNCATION_SIZE = reverseCharCount || 20
  const subject = string
  const truncatedProductVariantTitle =
    subject?.length > TRUNCATION_CHAR_SIZE + REVERSE_TRUNCATION_SIZE
      ? `${subject.slice(0, TRUNCATION_CHAR_SIZE)}...${subject.slice(
          -REVERSE_TRUNCATION_SIZE
        )}`
      : subject
  return truncatedProductVariantTitle
}
